import { useCallback, useEffect, useRef, useState } from 'react';
import maplibregl, { type MapGeoJSONFeature } from 'maplibre-gl';
import type { Feature } from 'geojson';
import 'maplibre-gl/dist/maplibre-gl.css';

import type { BoatProfile, Fix } from '../core/boat';
import type { LatLon } from '../core/geo';
import type { Waypoint } from '../core/route';
import type { DepthUnit, DistanceUnit } from '../core/units';
import type { PaletteName } from './palette';
import { PALETTES } from './palette';
import { type ChartManifest, buildChartStyle, queryableLayerIds } from './style';
import { webcamFeatures } from '../core/webcams';
import {
  canPredict,
  formatHorizon,
  predictPositions,
  predictionPath,
} from '../core/predict';
import { forwardCone } from '../core/forward';
import { HARBOUR_ATTRIBUTION, harbourFeatures } from '../core/harbours';
import {
  type ForwardScan,
  findForwardDangers,
  forwardDangerLabel,
} from './forwardDangers';
import { harbourIcon, vesselIcon, webcamIcon } from './markerIcons';
import {
  chartBaseUrl,
  glyphsUrl,
  isOverzoomed,
  registerCachedArchives,
  scaleDenominatorAt,
} from './chartSource';

export interface ChartViewProps {
  manifest: ChartManifest;
  boat: BoatProfile;
  palette: PaletteName;
  showSoundings: boolean;
  showLabels: boolean;
  /** Webcam markers. Not chart data — see src/core/webcams.ts. */
  showWebcams: boolean;
  /** Harbour and marina markers. Not chart data — see src/core/harbours.ts. */
  showHarbours: boolean;
  /** Dead-reckoning vector ahead of the boat. Not depth-checked — predict.ts. */
  prediction: { enabled: boolean; horizonsS: readonly number[] };
  /**
   * Mark charted dangers inside the sector ahead of the boat. Unlike the track
   * itself this *does* read the chart — see src/chart/forwardDangers.ts.
   */
  dangersAhead: boolean;
  /** Depth at or below which water ahead is flagged as shoal, metres; 0 is off. */
  cautionDepthM: number;
  /**
   * Full width of the sector searched ahead, degrees.
   *
   * One beam for the marks, the banner and the proximity alert — see the note on
   * `coneAngleDeg` in src/storage/settings.ts for why it is not split.
   */
  coneAngleDeg: number;
  /** Unit depth labels are drawn in. Tiles stay metric. */
  depthUnit: DepthUnit;
  /** Unit the scale bar is drawn in. */
  distanceUnit: DistanceUnit;
  fix: Fix | null;
  /** Waypoints of the route being viewed or edited; empty draws nothing. */
  routeWaypoints: readonly Waypoint[];
  /**
   * Points of the track being recorded or viewed; fewer than two draws nothing.
   *
   * Where the boat has been, as against the route's where it is going. Drawn
   * below everything else — history never covers the plan or the boat.
   */
  trackPoints: readonly LatLon[];
  /** Keep the vessel centred as it moves. */
  followVessel: boolean;
  /** Fired when the user pans, so the caller can drop follow mode. */
  onUserPan?: () => void;
  onOverzoomChange?: (overzoomed: boolean) => void;
  /**
   * Current zoom, the chart scale it corresponds to at this latitude, and the
   * visible extent — the last of which is what "download this area" means.
   */
  onViewChange?: (view: {
    zoom: number;
    scaleDenominator: number;
    region: { south: number; west: number; north: number; east: number };
  }) => void;
  onFeatureTap?: (features: MapGeoJSONFeature[], position: { lat: number; lon: number }) => void;
  /**
   * What is charted in the boat's path, whenever that changes. Fired only on a
   * change — the scan runs on every idle, and reporting each run would loop the
   * caller's state update straight back into another render.
   */
  onDangersAhead?: (scan: ForwardScan) => void;
  /** Raised when the map itself fails — most often chart tiles not loading. */
  onChartError?: (message: string) => void;
  /**
   * Hands the live map out so the route checker can read back loaded tiles.
   * Route checking cannot live in core: it must see what MapLibre has actually
   * rendered.
   */
  onMapReady?: (map: maplibregl.Map | null) => void;
}

const VESSEL_SOURCE = 'vessel';
const VESSEL_IMAGE = 'vessel-icon';
export const VESSEL_LAYER = 'vessel-boat';
const ROUTE_SOURCE = 'route';
const TRACK_SOURCE = 'track';
export const TRACK_LAYER = 'track-line';
const WEBCAM_SOURCE = 'webcam';
export const WEBCAM_LAYER = 'webcam-marker';
const WEBCAM_IMAGE = 'webcam-icon';
const HARBOUR_SOURCE = 'harbour';
export const HARBOUR_LAYER = 'harbour-marker';
const HARBOUR_IMAGE = 'harbour-icon';
const AHEAD_SOURCE = 'danger-ahead';
const AHEAD_HALO_LAYER = 'danger-ahead-halo';
const AHEAD_CAUTION_LAYER = 'danger-ahead-caution';
const AHEAD_LABEL_LAYER = 'danger-ahead-label';
/**
 * The last overlay layer installed, and therefore the one `installOverlays`
 * guards on. Guarding on anything earlier leaves a half-finished install that
 * every retry skips — see the comment there.
 */
export const AHEAD_LAYER = 'danger-ahead-mark';

/** Radius of the ring drawn round a danger in the boat's path, pixels. */
const AHEAD_RADIUS_PX = 7;

/**
 * GeoJSON for the vessel marker, accuracy ring and the vector ahead of it.
 *
 * The prediction *replaces* the old fixed-length course vector rather than
 * being drawn alongside it. Two collinear lines of different lengths, one of
 * them arbitrary, would invite exactly the wrong question — "which one is the
 * real one?" — so when the prediction is on it is the only line, and when it is
 * off a short heading stub takes its place so the boat still shows which way it
 * is pointing.
 */
export function vesselFeatures(
  fix: Fix | null,
  prediction: { enabled: boolean; horizonsS: readonly number[] },
): Feature[] {
  if (!fix) return [];

  const input = { position: fix, courseDeg: fix.courseDeg, speedMs: fix.speedMs };
  // The heading is carried on the position feature only when there is a real
  // course to carry. The hull symbol has a bow, and a bow points somewhere: with
  // no usable course the chart draws the plain circle instead, which claims
  // nothing about which way the boat is facing.
  const usableCourse = canPredict(input);

  const features: Feature[] = [{
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [fix.lon, fix.lat] },
    properties: {
      kind: 'position',
      accuracyM: Math.min(fix.accuracyM, 1000),
      ...(usableCourse ? { headingDeg: fix.courseDeg as number } : {}),
    },
  }];

  // Nothing at all below about a knot: reported course is a bearing between two
  // nearly identical positions there, and a line drawn from it is noise
  // presented as information.
  if (!usableCourse) return features;

  if (prediction.enabled && prediction.horizonsS.length > 0) {
    const path = predictionPath(input, prediction.horizonsS);
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: path.map((p) => [p.lon, p.lat]) },
      properties: { kind: 'prediction' },
    });

    for (const point of predictPositions(input, prediction.horizonsS)) {
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [point.position.lon, point.position.lat] },
        properties: { kind: 'horizon', label: formatHorizon(point.seconds) },
      });
    }
    return features;
  }

  // Prediction off: a short stub, long enough to read a heading from and too
  // short to be mistaken for a claim about where the boat will be.
  const stub = predictPositions(input, [30])[0];
  if (stub) {
    features.push({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: [[fix.lon, fix.lat], [stub.position.lon, stub.position.lat]],
      },
      properties: { kind: 'course' },
    });
  }
  return features;
}

/**
 * GeoJSON for the dangers in the boat's path.
 *
 * These are the only magenta rings left on the chart. The symbol did not get
 * cheaper to draw — it got rarer, and therefore worth looking at.
 */
export function forwardDangerFeatures(
  scan: ForwardScan,
  distanceUnit: DistanceUnit,
): Feature[] {
  return scan.dangers.map((danger) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [danger.position.lon, danger.position.lat] },
    properties: {
      key: danger.key,
      label: forwardDangerLabel(danger, distanceUnit),
      severity: danger.severity,
    },
  }));
}

/**
 * GeoJSON for a recorded track.
 *
 * One LineString, or nothing at all: a single point is not a track and drawing
 * a zero-length line for it puts a dot on the chart that looks like a charted
 * object. See src/core/track.ts.
 */
export function trackFeatures(points: readonly LatLon[]): Feature[] {
  if (points.length < 2) return [];
  return [{
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: points.map((p) => [p.lon, p.lat]) },
    properties: { kind: 'track' },
  }];
}

/** GeoJSON for the route line and its waypoints. */
function routeFeatures(waypoints: readonly Waypoint[]): Feature[] {
  const features: Feature[] = waypoints.map((waypoint, index) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [waypoint.lon, waypoint.lat] },
    properties: { id: waypoint.id, index: index + 1, name: waypoint.name ?? '' },
  }));

  if (waypoints.length >= 2) {
    features.unshift({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: waypoints.map((w) => [w.lon, w.lat]) },
      properties: {},
    });
  }
  return features;
}

/** Centre of the CHS V-ATL-B coverage — Halifax approaches. */
const FALLBACK_CENTRE: [number, number] = [-63.55, 44.62];
const FALLBACK_ZOOM = 11;

export function ChartView({
  manifest,
  boat,
  palette,
  showSoundings,
  showLabels,
  showWebcams,
  showHarbours,
  prediction,
  dangersAhead,
  cautionDepthM,
  coneAngleDeg,
  depthUnit,
  distanceUnit,
  fix,
  routeWaypoints,
  trackPoints,
  followVessel,
  onUserPan,
  onOverzoomChange,
  onViewChange,
  onFeatureTap,
  onDangersAhead,
  onChartError,
  onMapReady,
}: ChartViewProps): React.JSX.Element {
  const container = useRef<HTMLDivElement | null>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const [ready, setReady] = useState(false);
  // Why the last overlay install failed, kept so a failure that never resolves
  // can be reported once instead of vanishing into a catch.
  const lastInstallError = useRef<unknown>(null);
  const reportedInstallError = useRef(false);
  /** Re-entrancy guard: adding sources and images fires style events. */
  const installing = useRef(false);

  // One place builds the style, so the initial map and every later restyle
  // cannot drift apart.
  const styleFor = useCallback(
    () => buildChartStyle({
      manifest,
      chartBaseUrl: chartBaseUrl(),
      glyphsUrl: glyphsUrl(),
      boat,
      palette,
      showSoundings,
      showLabels,
      depthUnit,
    }),
    [manifest, boat, palette, showSoundings, showLabels, depthUnit],
  );
  const styleForRef = useRef(styleFor);
  styleForRef.current = styleFor;

  // Latest overlay data, so the overlays can be repopulated after a restyle
  // without waiting for the data effects to fire again.
  //
  // `palette` and `showLabels` are in here for a subtler reason: the map is
  // created in an effect with no dependencies, so anything `installOverlays`
  // closes over is frozen at first render. Reading the palette straight from
  // the prop meant the route and vessel kept their day colours after a switch
  // to night — the style underneath changed, the overlays did not.
  const overlayData = useRef({
    fix, routeWaypoints, trackPoints, palette, showLabels, showWebcams, showHarbours,
    prediction, boat, manifest, dangersAhead, cautionDepthM, coneAngleDeg, distanceUnit,
  });
  overlayData.current = {
    fix, routeWaypoints, trackPoints, palette, showLabels, showWebcams, showHarbours,
    prediction, boat, manifest, dangersAhead, cautionDepthM, coneAngleDeg, distanceUnit,
  };

  // Callbacks are held in refs so changing them never tears down the map.
  const handlers = useRef({
    onUserPan, onOverzoomChange, onViewChange, onFeatureTap, onChartError, onMapReady,
    onDangersAhead,
  });
  handlers.current = {
    onUserPan, onOverzoomChange, onViewChange, onFeatureTap, onChartError, onMapReady,
    onDangersAhead,
  };

  // What the last scan found, so a restyle can re-push it, and a signature of it
  // so an unchanged result neither redraws nor calls back. Without that the
  // caller's state update would render, settle, fire `idle`, and scan again.
  const lastScan = useRef<ForwardScan>({ dangers: [], total: 0 });
  const lastScanSignature = useRef<string | null>(null);

  /**
   * Look for charted dangers in the sector ahead and draw them.
   *
   * Runs on `idle` as well as on prop changes: the scan reads rendered tiles, so
   * running it the instant the fix moves would search a viewport whose new tiles
   * have not arrived — the same trap route checking documents.
   */
  const scanAhead = useCallback((): void => {
    const instance = map.current;
    if (!instance) return;
    const data = overlayData.current;
    const source = instance.getSource(AHEAD_SOURCE) as maplibregl.GeoJSONSource | undefined;
    // The overlay install has not landed yet. Do not record a signature: the
    // next style event retries, and this must not decide it has nothing to do.
    if (!source) return;

    const cone = data.dangersAhead && data.fix
      ? forwardCone(
        { position: data.fix, courseDeg: data.fix.courseDeg, speedMs: data.fix.speedMs },
        data.prediction.horizonsS,
        data.coneAngleDeg,
      )
      : null;
    const scan = findForwardDangers(
      instance, data.manifest, data.boat, cone, data.cautionDepthM,
    );

    // Distances are in the labels, so they enter the signature — coarsely, or
    // every metre run would redraw the chart.
    const signature = [
      data.distanceUnit,
      scan.total,
      ...scan.dangers.map((danger) => `${danger.key}@${Math.round(danger.distanceM / 25)}`),
    ].join('|');
    if (signature === lastScanSignature.current) return;
    lastScanSignature.current = signature;
    lastScan.current = scan;

    source.setData({
      type: 'FeatureCollection',
      features: forwardDangerFeatures(scan, data.distanceUnit),
    });
    // Dev-only, alongside `window.__map`: tools/verify-alerts.mjs reads the
    // scan it is checking rather than inferring it from marks on screen, which
    // would only ever see the part of the cone inside the viewport.
    if (import.meta.env.DEV) {
      (window as unknown as { __ahead?: ForwardScan }).__ahead = scan;
    }
    handlers.current.onDangersAhead?.(scan);
  }, []);

  // --- Create the map exactly once ----------------------------------------
  useEffect(() => {
    if (!container.current || map.current) return undefined;

    // Cache-backed readers must exist before the first tile request, or the
    // downloaded area is bypassed and fetched over the network anyway.
    registerCachedArchives(manifest, chartBaseUrl());

    const instance = new maplibregl.Map({
      container: container.current,
      style: styleForRef.current(),
      center: FALLBACK_CENTRE,
      zoom: FALLBACK_ZOOM,
      // Overzoom well past the charted maximum is allowed — sometimes you need
      // it — but the UI warns whenever it happens.
      maxZoom: 20,
      attributionControl: { compact: true },
      // Pitch and rotation are off: a tilted nautical chart is harder to read
      // and there is no terrain to justify it.
      pitchWithRotate: false,
      dragRotate: false,
      touchPitch: false,
    });

    // Chart tiles failing to load is a safety-relevant condition, not a console
    // curiosity — a blank chart must never be mistaken for empty water.
    instance.on('error', (event) => {
      const message = event.error?.message ?? String(event.error);
      // eslint-disable-next-line no-console
      console.error('[chart] MapLibre error:', message, event.error);
      handlers.current.onChartError?.(message);
    });

    /**
     * (Re)install the vessel and route overlays.
     *
     * `setStyle` discards every source and layer added after the style loaded,
     * so these must be re-created each time the style is rebuilt — which happens
     * whenever the draft, palette or label settings change. Without this the
     * boat and the route are silently absent: no error, just nothing drawn.
     * Idempotent, so it is safe to call on every style event.
     */
    const installOverlays = (): void => {
      // Guard on the LAST thing installed, not the first. Guarding on the route
      // source meant that if anything after it threw, the install was left
      // half-finished and every retry returned here immediately — permanently.
      // That is how the webcam layer came to be silently absent while the route
      // and vessel were fine. When a layer is added below the guard, this has to
      // move with it.
      if (instance.getLayer(AHEAD_LAYER)) return;
      // Several of the calls below fire style events synchronously, and those
      // events land back here. Without this the function re-enters itself
      // part-installed and recurses.
      if (installing.current) return;
      installing.current = true;
      try {
      // No isStyleLoaded() guard here on purpose. `styledata` fires *while* the
      // style is still loading, so gating on it meant every call bailed and the
      // overlays were never installed at all — the boat and the route silently
      // never appeared. Instead the add is attempted and allowed to throw; the
      // next style event retries.
      try {
      // Each add is guarded so a retry after a partial install does not throw
      // on the pieces that already landed.
      const addSourceOnce = (id: string, features: Feature[] = [], attribution?: string): void => {
        if (instance.getSource(id)) return;
        instance.addSource(id, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features },
          // Per-source attribution: MapLibre shows it only while the source is
          // in the style, which is what ODbL requires and what a global string
          // would get wrong when the layer is switched off.
          ...(attribution ? { attribution } : {}),
        });
      };
      const addLayerOnce = (layer: maplibregl.LayerSpecification): void => {
        if (instance.getLayer(layer.id)) return;
        instance.addLayer(layer);
      };
      addSourceOnce(VESSEL_SOURCE);
      addSourceOnce(ROUTE_SOURCE);
      addSourceOnce(TRACK_SOURCE);

      const colours = PALETTES[overlayData.current.palette];
      const textLayers = overlayData.current.showLabels;

      // Where the boat has been, under everything else. History must never
      // cover the plan, the boat, or a danger — and the track is the one
      // overlay that can be hours long and cross the whole screen.
      addLayerOnce({
        id: TRACK_LAYER,
        type: 'line',
        source: TRACK_SOURCE,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': colours.track,
          'line-width': 2,
          // Thin and slightly translucent on purpose: it is a record, not a
          // recommendation, and a solid line as bold as the route would invite
          // being followed back through water the chart never vouched for.
          'line-opacity': 0.85,
        },
      });

      // Route below the vessel: the boat must never be hidden by its own plan.
      addLayerOnce({
        id: 'route-casing',
        type: 'line',
        source: ROUTE_SOURCE,
        filter: ['==', ['geometry-type'], 'LineString'],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': colours.routeLineCasing, 'line-width': 6 },
      });

      addLayerOnce({
        id: 'route-line',
        type: 'line',
        source: ROUTE_SOURCE,
        filter: ['==', ['geometry-type'], 'LineString'],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': colours.routeLine, 'line-width': 2.5 },
      });

      addLayerOnce({
        id: 'route-waypoint',
        type: 'circle',
        source: ROUTE_SOURCE,
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': 6,
          'circle-color': colours.routeWaypoint,
          'circle-stroke-color': colours.routeLineCasing,
          'circle-stroke-width': 2,
        },
      });

      addLayerOnce({
        id: 'vessel-accuracy',
        type: 'circle',
        source: VESSEL_SOURCE,
        filter: ['==', ['get', 'kind'], 'position'],
        paint: {
          // Radius is the reported accuracy in real metres, so the circle
          // shrinks as the fix improves and honestly shows what is known.
          'circle-radius': [
            'interpolate', ['exponential', 2], ['zoom'],
            0, 0,
            20, ['/', ['get', 'accuracyM'], 0.075],
          ],
          'circle-color': colours.vesselAccuracy,
        },
      });

      addLayerOnce({
        id: 'vessel-course',
        type: 'line',
        source: VESSEL_SOURCE,
        filter: ['==', ['get', 'kind'], 'course'],
        paint: { 'line-color': colours.vessel, 'line-width': 2.5 },
      });

      // The prediction is dashed, and the route is solid. That difference is
      // load-bearing: the route has been checked against the chart and this has
      // not, so they must not look like the same kind of claim.
      addLayerOnce({
        id: 'vessel-prediction',
        type: 'line',
        source: VESSEL_SOURCE,
        filter: ['==', ['get', 'kind'], 'prediction'],
        layout: { 'line-cap': 'round' },
        paint: {
          'line-color': colours.vessel,
          'line-width': 2,
          'line-dasharray': [2, 2],
          'line-opacity': 0.9,
        },
      });

      addLayerOnce({
        id: 'vessel-horizon',
        type: 'circle',
        source: VESSEL_SOURCE,
        filter: ['==', ['get', 'kind'], 'horizon'],
        paint: {
          'circle-radius': 4,
          'circle-color': colours.vesselHalo,
          'circle-stroke-color': colours.vessel,
          'circle-stroke-width': 2,
        },
      });

      if (textLayers) {
        addLayerOnce({
          id: 'vessel-horizon-label',
          type: 'symbol',
          source: VESSEL_SOURCE,
          filter: ['==', ['get', 'kind'], 'horizon'],
          layout: {
            'text-field': ['get', 'label'] as unknown as string,
            'text-font': ['Noto Sans Regular'],
            'text-size': 11,
            'text-anchor': 'left',
            'text-offset': [0.6, 0],
            'text-allow-overlap': false,
            'text-ignore-placement': false,
          },
          paint: {
            'text-color': colours.vessel,
            'text-halo-color': colours.vesselHalo,
            'text-halo-width': 1.5,
          },
        });
      }

      // The boat, two ways. With a course it is a hull pointing where it is
      // going; without one it is a circle, because a bow drawn from a heading
      // the GPS invented would be a lie repeated every second. Exactly one of
      // these draws at a time — the filters are complementary.
      addLayerOnce({
        id: 'vessel-mark',
        type: 'circle',
        source: VESSEL_SOURCE,
        filter: ['all',
          ['==', ['get', 'kind'], 'position'],
          ['!', ['has', 'headingDeg']],
        ],
        paint: {
          'circle-radius': 7,
          'circle-color': colours.vessel,
          'circle-stroke-color': colours.vesselHalo,
          'circle-stroke-width': 2.5,
        },
      });

      if (!instance.hasImage(VESSEL_IMAGE)) {
        instance.addImage(
          VESSEL_IMAGE,
          vesselIcon({ body: colours.vessel, halo: colours.vesselHalo }),
          { pixelRatio: 2 },
        );
      }

      addLayerOnce({
        id: VESSEL_LAYER,
        type: 'symbol',
        source: VESSEL_SOURCE,
        filter: ['all',
          ['==', ['get', 'kind'], 'position'],
          ['has', 'headingDeg'],
        ],
        layout: {
          'icon-image': VESSEL_IMAGE,
          'icon-size': ['interpolate', ['linear'], ['zoom'], 8, 0.8, 15, 1.2],
          // Course over ground, degrees true. MapLibre rotates clockwise from
          // up, which is what a bearing already is, so it goes straight in.
          'icon-rotate': ['get', 'headingDeg'] as unknown as number,
          // Rotate with the chart, not with the screen.
          'icon-rotation-alignment': 'map',
          // The boat is never decluttered away, whatever else is on the chart.
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });

      // Webcams. Added last so a camera marker never hides the boat or a
      // hazard — it is the least important thing on the chart and sits above
      // nothing that matters.
      //
      // The image is added here rather than once at load: setStyle discards
      // style images along with everything else, and a symbol layer whose icon
      // is missing renders nothing at all while reporting no error.
      //
      // Added only when absent — never removed and re-added to recolour. Both
      // addImage and removeImage fire `styledata` synchronously, so doing that
      // re-entered this function before the layer below existed to guard it,
      // and recursed until the tile worker died with "Maximum call stack size
      // exceeded". A palette change always rebuilds the style anyway, which
      // discards the image, so the colours are picked up on the way back in.
      if (!instance.hasImage(WEBCAM_IMAGE)) {
        instance.addImage(
          WEBCAM_IMAGE,
          webcamIcon({ body: colours.webcam, halo: colours.webcamHalo }),
          { pixelRatio: 2 },
        );
      }

      addSourceOnce(WEBCAM_SOURCE, webcamFeatures() as Feature[]);

      addLayerOnce({
        id: WEBCAM_LAYER,
        type: 'symbol',
        source: WEBCAM_SOURCE,
        // Below zoom 8 the province is a handful of pixels wide and 79 markers
        // are a single purple smear. They are a harbour-scale convenience.
        minzoom: 8,
        layout: {
          'icon-image': WEBCAM_IMAGE,
          // Sized to be tappable with a thumb on a moving boat, not to be
          // pretty: about 20 px at harbour zoom.
          'icon-size': ['interpolate', ['linear'], ['zoom'], 8, 0.6, 13, 1],
          'icon-allow-overlap': false,
          // Unlike a hazard, a camera may be decluttered away — nothing is lost
          // by not seeing one. Rule 4 is about charted dangers, not annotations.
          'icon-ignore-placement': false,
          ...(textLayers
            ? {
              'text-field': ['get', 'name'] as unknown as string,
              'text-font': ['Noto Sans Regular'],
              'text-size': 11,
              'text-anchor': 'top' as const,
              'text-offset': [0, 1.1] as [number, number],
              'text-optional': true,
            }
            : {}),
        },
        paint: textLayers
          ? {
            'text-color': colours.webcam,
            'text-halo-color': colours.webcamHalo,
            'text-halo-width': 1.5,
          }
          : {},
      });
      instance.setLayoutProperty(
        WEBCAM_LAYER,
        'visibility',
        overlayData.current.showWebcams ? 'visible' : 'none',
      );

      // Harbours and marinas. Same reasoning as webcams: an app annotation above the
      // chart, below nothing that matters. Positions are OpenStreetMap's, so
      // the source carries the ODbL attribution.
      if (!instance.hasImage(HARBOUR_IMAGE)) {
        instance.addImage(
          HARBOUR_IMAGE,
          harbourIcon({ body: colours.harbour, halo: colours.harbourHalo }),
          { pixelRatio: 2 },
        );
      }

      addSourceOnce(HARBOUR_SOURCE, harbourFeatures() as Feature[], HARBOUR_ATTRIBUTION);

      addLayerOnce({
        id: HARBOUR_LAYER,
        type: 'symbol',
        source: HARBOUR_SOURCE,
        minzoom: 8,
        layout: {
          'icon-image': HARBOUR_IMAGE,
          'icon-size': ['interpolate', ['linear'], ['zoom'], 8, 0.6, 13, 1],
          // The pin points at its position, so it is anchored at the tip
          // rather than centred on the harbour.
          'icon-anchor': 'bottom',
          'icon-allow-overlap': false,
          'icon-ignore-placement': false,
          ...(textLayers
            ? {
              'text-field': ['get', 'name'] as unknown as string,
              'text-font': ['Noto Sans Regular'],
              'text-size': 11,
              'text-anchor': 'top' as const,
              'text-offset': [0, 0.4] as [number, number],
              'text-optional': true,
            }
            : {}),
        },
        paint: textLayers
          ? {
            'text-color': colours.harbour,
            'text-halo-color': colours.harbourHalo,
            'text-halo-width': 1.5,
          }
          : {},
      });
      instance.setLayoutProperty(
        HARBOUR_LAYER,
        'visibility',
        overlayData.current.showHarbours ? 'visible' : 'none',
      );

      // Dangers in the boat's path. Installed last, so nothing an app annotation
      // draws can cover the one thing on this chart that means "you are standing
      // into danger right now".
      addSourceOnce(AHEAD_SOURCE);

      addLayerOnce({
        id: AHEAD_HALO_LAYER,
        type: 'circle',
        source: AHEAD_SOURCE,
        paint: {
          'circle-radius': AHEAD_RADIUS_PX + 3,
          'circle-color': colours.dangerHalo,
          'circle-opacity': 0.85,
        },
      });

      // Shoal water the boat can float in. Deliberately NOT magenta: that colour
      // means danger on this chart and nothing else, and the whole value of the
      // ring is that it does not appear for water you can pass over. A thinner
      // dashed-looking ring in the contour colour says "shallower than it looks"
      // without claiming you cannot go there.
      addLayerOnce({
        id: AHEAD_CAUTION_LAYER,
        type: 'circle',
        source: AHEAD_SOURCE,
        filter: ['==', ['get', 'severity'], 'caution'],
        paint: {
          'circle-radius': AHEAD_RADIUS_PX - 1,
          'circle-color': 'transparent',
          'circle-stroke-color': colours.safetyContourLine,
          'circle-stroke-width': 2,
        },
      });

      if (textLayers) {
        addLayerOnce({
          id: AHEAD_LABEL_LAYER,
          type: 'symbol',
          source: AHEAD_SOURCE,
          layout: {
            'text-field': ['get', 'label'] as unknown as string,
            'text-font': ['Noto Sans Regular'],
            'text-size': 11,
            'text-anchor': 'left',
            'text-offset': [0.9, 0],
            // The RING is what must never be decluttered, and a circle layer
            // never is. The label is a convenience, and letting four of them
            // pile on top of each other over a cluster of rocks would recreate
            // the mess this feature exists to remove. The count in the banner is
            // what guarantees a dropped label is not a dropped danger.
            'text-allow-overlap': false,
            'text-ignore-placement': false,
            'text-padding': 4,
          },
          paint: {
            // Magenta belongs to danger alone, here as everywhere else.
            'text-color': [
              'case',
              ['==', ['get', 'severity'], 'caution'], colours.safetyContourLine,
              colours.danger,
            ],
            'text-halo-color': colours.dangerHalo,
            'text-halo-width': 1.6,
          },
        });
      }

      addLayerOnce({
        id: AHEAD_LAYER,
        type: 'circle',
        source: AHEAD_SOURCE,
        filter: ['==', ['get', 'severity'], 'danger'],
        paint: {
          'circle-radius': AHEAD_RADIUS_PX,
          'circle-color': 'transparent',
          'circle-stroke-color': colours.danger,
          'circle-stroke-width': 2.5,
        },
      });

      // Re-push whatever the overlays were showing before the style was
      // rebuilt, so a change of palette or draft does not blank the boat.
      redrawOverlays();
      } catch (error) {
        // Usually the style is simply not ready: a later styledata or idle
        // retries, and every add above is idempotent, so this is harmless.
        //
        // But it is not ALWAYS harmless, and swallowing it silently is how a
        // genuine mistake in a layer definition turns into "the marker just
        // does not appear" with nothing to go on. Log it and let the retry
        // decide whether it mattered.
        lastInstallError.current = error;
      }
      } finally {
        installing.current = false;
      }
    };

    const redrawOverlays = (): void => {
      const vessel = instance.getSource(VESSEL_SOURCE) as maplibregl.GeoJSONSource | undefined;
      const routeSource = instance.getSource(ROUTE_SOURCE) as maplibregl.GeoJSONSource | undefined;
      vessel?.setData({
        type: 'FeatureCollection',
        features: vesselFeatures(overlayData.current.fix, overlayData.current.prediction),
      });
      routeSource?.setData({
        type: 'FeatureCollection',
        features: routeFeatures(overlayData.current.routeWaypoints),
      });
      const trackSource = instance.getSource(TRACK_SOURCE) as maplibregl.GeoJSONSource | undefined;
      trackSource?.setData({
        type: 'FeatureCollection',
        features: trackFeatures(overlayData.current.trackPoints),
      });
      // Re-push the last scan rather than re-running it: the signature check in
      // scanAhead would see no change and leave the freshly-created source empty.
      const ahead = instance.getSource(AHEAD_SOURCE) as maplibregl.GeoJSONSource | undefined;
      ahead?.setData({
        type: 'FeatureCollection',
        features: forwardDangerFeatures(lastScan.current, overlayData.current.distanceUnit),
      });
    };

    instance.on('load', () => {
      installOverlays();
      setReady(true);
      reportView();
      handlers.current.onMapReady?.(instance);
    });

    // Fired after every setStyle. The overlays are gone by then and must be
    // rebuilt; installOverlays is idempotent so this is safe to call freely.
    // `idle` is the backstop: it only fires once the style really is ready.
    instance.on('styledata', installOverlays);
    instance.on('idle', () => {
      installOverlays();
      // Tiles are loaded by now, which is the only moment the sector ahead can
      // honestly be searched.
      scanAhead();
      // By `idle` the style really is ready, so an install that still has not
      // succeeded is a bug rather than a race. Report it once — the failure
      // mode otherwise is a layer that silently never draws.
      if (!instance.getLayer(AHEAD_LAYER) && lastInstallError.current && !reportedInstallError.current) {
        reportedInstallError.current = true;
        // eslint-disable-next-line no-console
        console.error('[chart] overlays failed to install:', lastInstallError.current);
      }
    });

    const reportView = (): void => {
      const zoom = instance.getZoom();
      const bounds = instance.getBounds();
      handlers.current.onOverzoomChange?.(isOverzoomed(manifest, zoom));
      handlers.current.onViewChange?.({
        zoom,
        scaleDenominator: scaleDenominatorAt(zoom, instance.getCenter().lat),
        region: {
          south: bounds.getSouth(),
          west: bounds.getWest(),
          north: bounds.getNorth(),
          east: bounds.getEast(),
        },
      });
    };
    instance.on('zoom', reportView);
    instance.on('moveend', reportView);

    // `dragstart` fires only for user gestures, not for programmatic easeTo,
    // which is exactly the distinction follow-mode needs.
    instance.on('dragstart', () => handlers.current.onUserPan?.());

    instance.on('click', (event) => {
      const ids = queryableLayerIds(manifest).filter((id) => instance.getLayer(id));
      // Webcams are queried first so a camera sitting over a charted feature
      // still wins the tap: the marker is what the skipper aimed at, and it is
      // the only thing on screen that does nothing unless you hit it.
      if (instance.getLayer(WEBCAM_LAYER) && overlayData.current.showWebcams) {
        ids.unshift(WEBCAM_LAYER);
      }
      if (instance.getLayer(HARBOUR_LAYER) && overlayData.current.showHarbours) {
        ids.unshift(HARBOUR_LAYER);
      }
      const features = instance.queryRenderedFeatures(event.point, { layers: ids });
      handlers.current.onFeatureTap?.(features, { lat: event.lngLat.lat, lon: event.lngLat.lng });
    });

    // A scale bar in the user's own distance unit. Reading distance off the
    // chart by eye is a basic navigation act and it needs a reference.
    instance.addControl(
      new maplibregl.ScaleControl({
        maxWidth: 110,
        unit: distanceUnit === 'nm' ? 'nautical' : distanceUnit === 'mi' ? 'imperial' : 'metric',
      }),
      'bottom-right',
    );

    map.current = instance;
    if (import.meta.env.DEV) {
      (window as unknown as { __map?: maplibregl.Map }).__map = instance;
    }

    return () => {
      handlers.current.onMapReady?.(null);
      instance.remove();
      map.current = null;
      setReady(false);
    };
    // Intentionally created once: the style is swapped in place below rather
    // than by rebuilding the map, which would lose the viewport.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Rebuild the style when the boat or display options change ----------
  // This is the mechanism behind draft-aware shading: changing the draft
  // recompiles the depth thresholds and hazard filters into a new style.
  useEffect(() => {
    if (!map.current || !ready) return;
    const instance = map.current;

    instance.setStyle(styleFor(), { diff: true });
  }, [styleFor, ready]);

  // --- Overlay data --------------------------------------------------------
  // The feature builders are shared with the post-restyle redraw, so the
  // overlays cannot end up showing something different depending on which path
  // last touched them.
  useEffect(() => {
    if (!map.current || !ready) return;
    const source = map.current.getSource(VESSEL_SOURCE) as maplibregl.GeoJSONSource | undefined;
    source?.setData({ type: 'FeatureCollection', features: vesselFeatures(fix, prediction) });
  }, [fix, prediction, ready]);

  useEffect(() => {
    if (!map.current || !ready) return;
    const source = map.current.getSource(ROUTE_SOURCE) as maplibregl.GeoJSONSource | undefined;
    source?.setData({ type: 'FeatureCollection', features: routeFeatures(routeWaypoints) });
  }, [routeWaypoints, ready]);

  useEffect(() => {
    if (!map.current || !ready) return;
    const source = map.current.getSource(TRACK_SOURCE) as maplibregl.GeoJSONSource | undefined;
    source?.setData({ type: 'FeatureCollection', features: trackFeatures(trackPoints) });
  }, [trackPoints, ready]);

  // The sector ahead moves with the boat and changes shape with the draft, the
  // horizons and the unit the labels are drawn in. `idle` catches the rest.
  useEffect(() => {
    if (!map.current || !ready) return;
    scanAhead();
  }, [fix, boat, dangersAhead, cautionDepthM, coneAngleDeg, prediction, distanceUnit, ready, scanAhead]);

  // Toggling webcams flips layer visibility rather than rebuilding the style.
  // The style is expensive to rebuild and a restyle drops every overlay, so
  // turning markers off would briefly blank the boat and the route with it.
  useEffect(() => {
    if (!map.current || !ready) return;
    const instance = map.current;
    if (!instance.getLayer(WEBCAM_LAYER)) return;
    instance.setLayoutProperty(WEBCAM_LAYER, 'visibility', showWebcams ? 'visible' : 'none');
  }, [showWebcams, ready]);

  useEffect(() => {
    if (!map.current || !ready) return;
    const instance = map.current;
    if (!instance.getLayer(HARBOUR_LAYER)) return;
    instance.setLayoutProperty(HARBOUR_LAYER, 'visibility', showHarbours ? 'visible' : 'none');
  }, [showHarbours, ready]);

  // --- Follow mode ---------------------------------------------------------
  useEffect(() => {
    if (!map.current || !ready || !followVessel || !fix) return;
    map.current.easeTo({ center: [fix.lon, fix.lat], duration: 500 });
  }, [fix, followVessel, ready]);

  return <div ref={container} className="chart-view" data-testid="chart-view" />;
}

/** Imperative helper for "centre on my boat" without enabling follow mode. */
export function useChartCentring(): {
  centreOn: (map: maplibregl.Map | null, position: { lat: number; lon: number }) => void;
} {
  const centreOn = useCallback(
    (map: maplibregl.Map | null, position: { lat: number; lon: number }) => {
      map?.easeTo({ center: [position.lon, position.lat], duration: 500 });
    },
    [],
  );
  return { centreOn };
}
